import { matchPath } from 'react-router'

import type { PageContextPayload } from '@/models/agentConversation'
import { AUTOMATION_FOLLOW_UP_PARAM } from '@/models/outboundCallAutomation'

/**
 * Which record a page is about. Must stay in sync with `ENTITY_RESOLVERS` in
 * `backend/insure/services/agent/internal_agent/page_context.py` — the backend drops
 * any type it doesn't know, so a typo silently degrades to page-only context.
 *
 * Ask Cai reuses these names against its own, org-scoped resolver registry in
 * `ask_cai_agent/page_context.py`, which knows only a subset of them.
 */
export enum PageContextEntity {
  ORGANISATION = 'organisation',
  SALES_LEAD = 'sales_lead',
  INSURANCE_APPLICATION = 'insurance_application',
  CLAIM = 'claim',
  POLICY_CHANGE = 'policy_change',
  MOTOR_CARRIER_DOT = 'motor_carrier_dot',
}

export interface PageContextRoute {
  /** React Router path pattern, matched in array order. */
  pattern: string
  /** Stable key for this page, sent to the backend and used in analytics. */
  page: string
  /** What the chip says when the page carries no record. */
  label: string
  entityType?: PageContextEntity
  /** Route param holding the record id. */
  entityParam?: string
  /**
   * Query param holding the record id, for pages that open a record in a
   * drawer rather than navigating (`/sales-leads/pipeline?lead=`). Reported as
   * the record, never as a filter.
   */
  entityQueryParam?: string
}

/**
 * Query params that open an overlay rather than describing the page. Reporting
 * them as "active filters" tells the agent the list is narrowed when it isn't.
 * `thread` has no resolver, so it is dropped rather than reported as a record.
 */
const IGNORED_SEARCH_PARAMS = new Set(['call_lead', 'thread', AUTOMATION_FOLLOW_UP_PARAM])

const MAX_FILTERS = 12

export interface PageContextValue {
  page: string
  label: string
  path: string
  entityType?: PageContextEntity
  entityId?: string
  /** The page's own query string — what the list is filtered/sorted by. */
  filters: Record<string, string>
}

export function toPageContextPayload(context: PageContextValue): PageContextPayload {
  return {
    page: context.page,
    label: context.label,
    path: context.path,
    entity_type: context.entityType ?? '',
    entity_id: context.entityId ?? '',
    filters: context.filters,
  }
}

/**
 * What the agent should be told about a page. First match wins, so the `routes`
 * array's order IS the precedence — the staff and customer maps each pass their
 * own.
 */
export function pageContextFor(
  pathname: string,
  searchParams: URLSearchParams,
  routes: PageContextRoute[]
): PageContextValue | null {
  for (const route of routes) {
    const match = matchPath({ path: route.pattern, end: true }, pathname)
    if (!match) continue
    const entityId =
      (route.entityParam
        ? match.params[route.entityParam]
        : route.entityQueryParam
          ? searchParams.get(route.entityQueryParam)
          : undefined) || undefined

    const filters: Record<string, string> = {}
    for (const [key, value] of searchParams.entries()) {
      if (Object.keys(filters).length >= MAX_FILTERS) break
      if (IGNORED_SEARCH_PARAMS.has(key) || key === route.entityQueryParam || !value) continue
      filters[key] = value
    }

    return {
      page: route.page,
      label: route.label,
      path: pathname,
      entityType: entityId ? route.entityType : undefined,
      entityId,
      filters,
    }
  }
  return null
}
